import { useCallback, useEffect, useState } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { mapTeamError, type TeamMember, type Role, type Capability } from '../lib/teamView'

const API = import.meta.env.VITE_API_URL ?? ''

export interface TeamAccess {
  can_manage_team: boolean
  is_owner: boolean
  role: Role
  active: boolean
}
export interface OrgLocation {
  id: string
  name: string
}
export interface MutationResult {
  ok: boolean
  error?: string
}

async function readError(res: Response): Promise<string> {
  let body: any = {}
  try {
    body = await res.json()
  } catch {
    /* non-JSON */
  }
  return mapTeamError({ status: res.status, code: body?.code, error: body?.error })
}

export function useTeam() {
  const authFetch = useAuthFetch()
  const [access, setAccess] = useState<TeamAccess | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [locations, setLocations] = useState<OrgLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadMembers = useCallback(
    async (isStale?: () => boolean) => {
      const res = await authFetch(`${API}/api/v1/team/members`)
      if (!res.ok) throw new Error(await readError(res))
      const data = await res.json()
      if (!isStale?.()) setMembers(data.data?.members ?? [])
    },
    [authFetch],
  )

  const loadAll = useCallback(
    async (isStale?: () => boolean) => {
      setLoading(true)
      setError(null)
      try {
        const meRes = await authFetch(`${API}/api/v1/team/me`)
        const me = meRes.ok ? (await meRes.json()).data : null
        if (isStale?.()) return
        setAccess(me)
        if (!me?.can_manage_team) return // guarded — don't fetch roster
        // Roster + locations for the picker.
        const locRes = await authFetch(`${API}/api/v1/locations`)
        if (locRes.ok) {
          const locData = await locRes.json()
          const rows = locData.data?.locations ?? locData.data ?? locData.locations ?? []
          if (!isStale?.()) setLocations(rows.map((l: any) => ({ id: l.id, name: l.name })))
        }
        await loadMembers(isStale)
      } catch (err: any) {
        if (!isStale?.()) setError(err.message)
      } finally {
        if (!isStale?.()) setLoading(false)
      }
    },
    [authFetch, loadMembers],
  )

  useEffect(() => {
    let cancelled = false
    loadAll(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [loadAll])

  const mutate = useCallback(
    async (url: string, method: string, body?: unknown): Promise<MutationResult> => {
      try {
        const res = await authFetch(`${API}${url}`, {
          method,
          body: body ? JSON.stringify(body) : undefined,
        })
        if (!res.ok) return { ok: false, error: await readError(res) }
        await loadMembers()
        return { ok: true }
      } catch (err: any) {
        return { ok: false, error: err?.message || 'Request failed.' }
      }
    },
    [authFetch, loadMembers],
  )

  const invite = (input: {
    email: string
    role: Role
    all_locations: boolean
    location_ids: string[]
    permissions: Partial<Record<Capability, boolean>>
  }) => mutate('/api/v1/team/invitations', 'POST', input)

  const updateMember = (
    id: string,
    patch: { role?: Role; all_locations?: boolean; permissions?: Record<string, boolean>; active?: boolean },
  ) => mutate(`/api/v1/team/members/${id}`, 'PATCH', patch)

  const setMemberLocations = (id: string, location_ids: string[]) =>
    mutate(`/api/v1/team/members/${id}/locations`, 'PUT', { location_ids })

  return {
    access,
    members,
    locations,
    loading,
    error,
    invite,
    updateMember,
    setMemberLocations,
    refetch: loadAll,
  }
}
